"use strict";

(() => {
	class Inflation_Calculator {
		/** @var {HTMLElement} */
		#$widget;

		/** @var {NodeListOf<HTMLInputElement>} */
		#$inflationTypeInputs;

		/** @var {HTMLInputElement} */
		#$startYearInput;

		/** @var {HTMLInputElement} */
		#$amountInput;

		/** @var {HTMLLabelElement} */
		#$endYearLabel;

		/** @var {HTMLInputElement} */
		#$endYearInput;

		/** @var {HTMLInputElement} */
		#$inflationRateInput;

		/** @var {HTMLElement} */
		#$result;

		/** @var {HTMLElement} */
		#$resultContent;

		/** @var {HTMLTemplateElement} */
		#$resultTemplateFuture;

		/** @var {HTMLTemplateElement} */
		#$resultTemplatePast;

		/** @var {object} */
		#inflationData;


		constructor($widget, inflationData) {
			this.#$widget       = $widget;
			this.#inflationData = inflationData;
		}

		initialize() {
			// Initialite selectors.
			this.#$inflationTypeInputs = this.#$widget.querySelectorAll(".dan-inputs-inflation-type input[type='radio']");
			this.#$startYearInput      = this.#$widget.querySelector(".dan-input-start-year");
			this.#$amountInput         = this.#$widget.querySelector(".dan-input-amount");
			this.#$endYearLabel        = this.#$widget.querySelector(".dan-label-end-year");
			this.#$endYearInput        = this.#$widget.querySelector(".dan-input-end-year");
			this.#$inflationRateInput  = this.#$widget.querySelector(".dan-input-inflation-rate");

			this.#$result               = this.#$widget.querySelector(".dan-result");
			this.#$resultContent        = this.#$result.querySelector(".dan-result-content");
			this.#$resultTemplateFuture = this.#$widget.querySelector(".dan-template-result-future");
			this.#$resultTemplatePast   = this.#$widget.querySelector(".dan-template-result-past");

			// Add handlers to events,
			this.#$widget.addEventListener("submit", (this.#onFormSubmit).bind(this));

			for (const $input of this.#$inflationTypeInputs) {
				$input.addEventListener("change", (this.#onInflationTypeChange).bind(this));
			}

			// Initialize quantity inputs.
			const $quantityGroups = this.#$widget.querySelectorAll(".dan-quantity-group");

			for (const $quantityGroup of $quantityGroups) {
				$quantityGroup.quantityInput = new Dan_QuantityInput($quantityGroup);
				$quantityGroup.quantityInput.initialize();
			}
		}

		#onInflationTypeChange() {
			const inputs = this.getInputs();

			// Set end year label.
			const labelDataAttr             = "data-label-" + inputs.inflationType;
			this.#$endYearLabel.textContent = this.#$endYearLabel.getAttribute(labelDataAttr);

			// Set start and end year values.
			const valueDataAttr         = "data-value-" + inputs.inflationType;
			this.#$startYearInput.value = this.#$startYearInput.getAttribute(valueDataAttr);
			this.#$endYearInput.value   = this.#$endYearInput.getAttribute(valueDataAttr);

			// Refresh inflation rate input visibility.
			this.#refreshInflationRateInputVisibility();
		}

		#refreshInflationRateInputVisibility() {
			const $wrapper = this.#$widget.querySelector(".dan-wrapper-inflation-rate");
			const shown    = $wrapper.classList.contains("-active");
			const inputs   = this.getInputs();

			if (inputs.inflationType === "future" && !shown) {
				$wrapper.style.height = "0px";

				setTimeout(() => {
					$wrapper.style.height = $wrapper.scrollHeight + "px";
				}, 25);

				setTimeout(() => {
					$wrapper.style.height = "auto";
					$wrapper.classList.add("-active");
				}, 175);
			} else if (inputs.inflationType !== "future" && shown) {
				$wrapper.style.height = $wrapper.scrollHeight + "px";

				setTimeout(() => {
					$wrapper.style.height = "0px";
				}, 25);

				setTimeout(() => {
					$wrapper.classList.remove("-active");
				}, 175);
			}
		}

		#onFormSubmit(event) {
			event.preventDefault();
			const inputs = this.getInputs();

			if (inputs.inflationType === "future") {
				this.#processFutureInflation(inputs);
			} else {
				this.#processPastInflation(inputs);
			}
		}

		#processFutureInflation(inputs) {
			if (
				inputs.startYear === null
				|| inputs.endYear === null
				|| inputs.amount === null
				|| inputs.inflationRate === null
			) {
				alert("Prosím, vyplňte správně vechna pole.");
				return;
			}

			if (inputs.startYear >= inputs.endYear) {
				alert("Konečný rok musí být větší než počáteční rok.");
				return;
			}

			const resultAmount = this.#calcFutureValue(
				inputs.amount,
				inputs.startYear,
				inputs.endYear,
				inputs.inflationRate
			);

			this.#setResult(
				"future",
				inputs.startYear,
				inputs.endYear,
				inputs.amount,
				resultAmount,
				inputs.inflationRate
			);
		}

		#processPastInflation(inputs) {
			if (
				inputs.startYear === null
				|| inputs.endYear === null
				|| inputs.amount === null
			) {
				alert("Prosím, vyplňte správně vechna pole.");
				return;
			}

			if (inputs.startYear >= inputs.endYear) {
				alert("Konečný rok musí být větší než počáteční rok.");
				return;
			}

			const inflationRate = this.#calcAverageInflationRate(
				inputs.startYear,
				inputs.endYear
			);

			if (inflationRate === null) {
				const minMaxYears = this.#getMinAndMaxDataYears();

				if (minMaxYears) {
					alert("Pro zadané období nemáme k dispozici údaje o inflaci. Prosím, zvolte období mezi léty " + minMaxYears.min + " a " + minMaxYears.max + ".");
				} else {
					alert("Pro zadané období nemáme k dispozici údaje o inflaci.");
				}

				return;
			}

			const resultAmount = this.#calcFutureValue(
				inputs.amount,
				inputs.startYear,
				inputs.endYear,
				inflationRate
			);

			this.#setResult(
				"past",
				inputs.startYear,
				inputs.endYear,
				inputs.amount,
				resultAmount,
				inflationRate
			);
		}

		#getMinAndMaxDataYears() {
			let years;
			years = Object.keys(this.#inflationData);
			years = years.map((year) => parseInt(year));

			if (years.length === 0) {
				return null;
			}

			const min   = Math.min(...years);
			const max   = Math.max(...years);

			return {
				min,
				max
			};
		}

		#calcAverageInflationRate(startYear, endYear) {
			// Check inflation data.
			if (this.#inflationData === null) {
				return null;
			}

			// Build array of inflation rates.
			let inflationRates = [];

			for (let year = (startYear + 1); year <= endYear; year++) {
				if (!this.#inflationData[year]) {
					return null;
				}

				inflationRates.push(this.#inflationData[year]);
			}

			if (inflationRates.length === 0) {
				return null;
			}

			// Calculate average inflation rate.
			let inflationRatesSum = 0.0;

			for (const rate of inflationRates) {
				inflationRatesSum += rate;
			}

			return (Math.round(inflationRatesSum / inflationRates.length * 100) / 100);
		}

		#calcFutureValue(amount, startYear, endYear, inflationRate) {
			let resultAmount = amount;

			for (let year = endYear; year > startYear; year--) {
				const devaluation = (resultAmount * (inflationRate / 100));
				resultAmount      -= devaluation;
			}

			return resultAmount;
		}

		#setResult(
			type,
			startYear,
			endYear,
			entryAmount,
			resultAmount,
			inflationRate
		) {
			if (this.#$result.classList.contains("-active")) {
				this.#slideResultUp(() => {
					this.#setResultValues(
						type,
						startYear,
						endYear,
						entryAmount,
						resultAmount,
						inflationRate
					);

					this.#slideResultDown();
				});
			} else {
				this.#setResultValues(
					type,
					startYear,
					endYear,
					entryAmount,
					resultAmount,
					inflationRate
				);

				this.#slideResultDown();
			}
		}

		#setResultValues(
			type,
			startYear,
			endYear,
			entryAmount,
			resultAmount,
			inflationRate
		) {
			const $template = (type === "future")
				? this.#$resultTemplateFuture
				: this.#$resultTemplatePast;

			this.#$resultContent.innerHTML = $template.innerHTML;

			const $startYear     = this.#$resultContent.querySelector(".-start-year");
			const $endYear       = this.#$resultContent.querySelector(".-end-year");
			const $entryAmount   = this.#$resultContent.querySelector(".-entry-amount");
			const $resultAmount  = this.#$resultContent.querySelector(".-result-amount");
			const $inflationRate = this.#$resultContent.querySelector(".-inflation-rate");

			$startYear.textContent     = startYear;
			$endYear.textContent       = endYear;
			$entryAmount.textContent   = Dan_Utilities.formatPrice(entryAmount) + " Kč";
			$resultAmount.textContent  = Dan_Utilities.formatPrice(resultAmount) + " Kč";
			$inflationRate.textContent = inflationRate + " %";
		}

		#slideResultUp(callback = null) {
			this.#$result.style.height = this.#$result.scrollHeight + "px";

			setTimeout(() => {
				this.#$result.style.height = "0";
			}, 25);

			setTimeout(() => {
				this.#$result.classList.remove("-active");

				if (typeof callback === "function") {
					callback();
				}
			}, 175);
		}

		#slideResultDown(callback = null) {
			this.#$result.style.height = this.#$result.scrollHeight + "px";

			setTimeout(() => {
				this.#$result.style.height = "auto";
				this.#$result.classList.add("-active");

				if (typeof callback === "function") {
					callback();
				}
			}, 150);
		}

		getInputs() {
			// Inflation type.
			let inflationType = null;

			for (const $input of this.#$inflationTypeInputs) {
				if ($input.checked) {
					inflationType = $input.value;
					break;
				}
			}

			// Start year.
			let startYear = parseInt(this.#$startYearInput.value);

			if (isNaN(startYear) || startYear < 0) {
				startYear = null;
			}

			// Amount.
			let amount;
			amount = this.#$amountInput.value || this.#$amountInput.placeholder;
			amount = parseFloat(amount);

			if (isNaN(amount) || amount < 0) {
				amount = null;
			}

			// End year.
			let endYear = parseInt(this.#$endYearInput.value);

			if (isNaN(endYear) || endYear < 0) {
				endYear = null;
			}

			// Inflation rate.
			let inflationRate;

			if (inflationType === "future") {
				inflationRate = this.#$inflationRateInput.value || this.#$inflationRateInput.placeholder;
				inflationRate = parseFloat(inflationRate);

				if (isNaN(inflationRate) || inflationRate < 0) {
					inflationRate = null;
				}
			} else {
				inflationRate = null;
			}

			// Return.
			return {
				inflationType,
				startYear,
				amount,
				endYear,
				inflationRate
			};
		}
	}

	// Initialize widgets.
	const $widgets = document.querySelectorAll(".dan-inflation-calculator");

	for (const $widget of $widgets) {
		let inflationData;
		inflationData = $widget.getAttribute("data-inflation-data") || null;
		inflationData = (inflationData) ? JSON.parse(inflationData) : null;

		$widget.calculator = new Inflation_Calculator($widget, inflationData);
		$widget.calculator.initialize();
	}
})();
